import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { adminApi } from "@/api/admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { formatDate, formatMonth } from "@/lib/dates";
import type { ReteQuotaResponsabile } from "@/types/admin";
import type { QuotaReteResponse, StatoQuotaRete } from "@/types/rete";

// Frase approvata per la pagina del responsabile, riusata parola per parola:
// per la console non ne è stata approvata una propria.
const ERRORE = "Impossibile caricare la quota.";

function euro(n: number): string {
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

const STATO: Record<StatoQuotaRete, string> = {
  maturata: "Maturata",
  azzerata: "Azzerata",
  stornata: "Stornata",
};

function meseDaUrl(sp: URLSearchParams): { anno: number; mese: number } {
  const oggi = new Date();
  const anno = Number(sp.get("anno")) || oggi.getFullYear();
  const mese = Number(sp.get("mese")) || oggi.getMonth() + 1;
  return { anno, mese: Math.min(12, Math.max(1, mese)) };
}

function spostaMese(anno: number, mese: number, delta: number): { anno: number; mese: number } {
  const d = new Date(anno, mese - 1 + delta, 1);
  return { anno: d.getFullYear(), mese: d.getMonth() + 1 };
}

/** Quote del mese, un responsabile per riga. «Dettaglio» apre sotto la tabella le
 *  righe di quel responsabile: le stesse che lui vede in `/rete/quota`. */
export function ReteQuote() {
  const [sp, setSp] = useSearchParams();
  const { anno, mese } = meseDaUrl(sp);
  const [rows, setRows] = useState<ReteQuotaResponsabile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apertoId, setApertoId] = useState<number | null>(null);
  const [apertoNome, setApertoNome] = useState("");
  const [dettaglio, setDettaglio] = useState<QuotaReteResponse | null>(null);
  const [erroreDettaglio, setErroreDettaglio] = useState<string | null>(null);
  // Guardia contro risposte fuori ordine: solo l'ultima `apriDettaglio` in corso può
  // scrivere lo stato. Chiudere o cambiare mese invalida quelle precedenti.
  const token = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    // Cambiando mese il dettaglio aperto parlerebbe di un altro mese: si chiude.
    token.current++;
    setApertoId(null);
    setDettaglio(null);
    setErroreDettaglio(null);
    adminApi.reteQuote(anno, mese)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch(() => { if (!cancelled) setError(ERRORE); });
    return () => { cancelled = true; };
  }, [anno, mese]);

  function vaiAlMese(delta: number) {
    const m = spostaMese(anno, mese, delta);
    // Forma funzionale: `?tab=quote` deve sopravvivere al cambio di mese.
    setSp((prev) => {
      prev.set("anno", String(m.anno));
      prev.set("mese", String(m.mese));
      return prev;
    });
  }

  async function apriDettaglio(r: ReteQuotaResponsabile) {
    const mio = ++token.current;
    setApertoId(r.responsabile_id);
    setApertoNome(r.nome);
    setDettaglio(null);
    setErroreDettaglio(null);
    try {
      const d = await adminApi.reteQuoteResponsabile(r.responsabile_id, anno, mese);
      if (mio === token.current) setDettaglio(d);
    } catch {
      if (mio === token.current) setErroreDettaglio(ERRORE);
    }
  }

  function chiudiDettaglio() {
    token.current++;
    setApertoId(null);
    setDettaglio(null);
    setErroreDettaglio(null);
  }

  const meseMinuscolo = formatMonth(`${anno}-${String(mese).padStart(2, "0")}`);
  const meseLeggibile = meseMinuscolo.charAt(0).toUpperCase() + meseMinuscolo.slice(1);

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Quote</h3>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" aria-label="Mese prima" onClick={() => vaiAlMese(-1)}>
              <ChevronLeft size={16} />
            </Button>
            <span className="min-w-[9rem] text-center text-sm font-medium text-foreground">
              {meseLeggibile}
            </span>
            <Button size="sm" variant="outline" aria-label="Mese dopo" onClick={() => vaiAlMese(1)}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>

        {error ? (
          <p className="px-4 py-6 text-sm text-destructive">{error}</p>
        ) : rows === null ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Caricamento...</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Nessun responsabile</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Responsabile</TableHead>
                  <TableHead className="text-right">Pratiche</TableHead>
                  <TableHead className="text-right">Quota</TableHead>
                  <TableHead className="text-right hidden md:table-cell">Da recuperare</TableHead>
                  <TableHead className="hidden md:table-cell">Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.responsabile_id} className={r.attivo ? "" : "opacity-60"}>
                    <TableCell className="font-medium text-foreground">{r.nome}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.n_pratiche}</TableCell>
                    <TableCell className="text-right tabular-nums">{euro(r.totale)}</TableCell>
                    <TableCell className="text-right tabular-nums hidden md:table-cell">
                      {euro(r.da_recuperare)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge
                        variant="outline"
                        className={r.attivo ? "border-emerald-400 text-emerald-700" : "border-border text-muted-foreground"}
                      >
                        {r.attivo ? "Attivo" : "Disattivato"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Senza pratiche il dettaglio direbbe solo «Nessuna quota nel mese»:
                          la riga lo dice già con 0 e €0,00. */}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={r.n_pratiche === 0}
                        onClick={() => void apriDettaglio(r)}
                      >
                        Dettaglio
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {apertoId !== null && (
        <section className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
            <h4 className="text-sm font-semibold text-foreground">{apertoNome}</h4>
            <Button size="sm" variant="outline" onClick={chiudiDettaglio}>Chiudi</Button>
          </div>

          {erroreDettaglio ? (
            <p className="px-4 py-6 text-sm text-destructive">{erroreDettaglio}</p>
          ) : dettaglio === null ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">Caricamento...</p>
          ) : dettaglio.righe.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">Nessuna quota nel mese</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pratica</TableHead>
                    <TableHead className="hidden md:table-cell">Agenzia</TableHead>
                    <TableHead className="hidden md:table-cell">Competenza</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dettaglio.righe.map((r) => (
                    <TableRow
                      key={r.opportunity_id}
                      className={r.stato === "maturata" ? "" : "line-through text-muted-foreground"}
                    >
                      <TableCell className="font-medium text-foreground">{r.pratica_nome ?? "—"}</TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {r.agenzia_nome}
                      </TableCell>
                      <TableCell className="hidden md:table-cell tabular-nums">
                        {formatDate(r.competenza)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{euro(r.importo)}</TableCell>
                      <TableCell>{STATO[r.stato]}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
